/*
================================================================================
FILE HEADER — apps/studio-web/src/components/ExecutionPanel.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Execution page (Phase 3) | Created — Mimiq-     |
| 2  | 2026-06-25 | Claude Sonnet 4.6 | Run Config Sheet         | Added runConfig prop;|
|    |            |                   |                          | shows browser badge  |
|    |            |                   |                          | in header            |
| 3  | 2026-06-25 | Claude Sonnet 4.6 | Option B — per-step      | Added relayStatus +  |
|    |            |                   | screenshots              | onConnectRelay props;|
|    |            |                   |                          | connect UI for CLI   |
|    |            |                   |                          | relay; currentStep   |
|    |            |                   |                          | label display        |
| 4  | 2026-07-02 | Claude Sonnet 4.6 | Tier-2 Ollama (Phase 3)  | Added tier2Enabled + |
|    |            |                   |                          | onRequestAiTriage +  |
|    |            |                   |                          | aiTriageResults props|
|    |            |                   |                          | "🤖 Analyze" button  |
|    |            |                   |                          | on failed steps      |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- ExecutionQueueItem — per-test state during a run
- ExecutionPanel     — right-side pane of the Execution tab

IMPORTANT POINTS
----------------
1. The mirror (BrowserMirror) lives in App.tsx and occupies the left 50%.
   This component is the right 50% only.
2. runStatus transitions: idle → running → (paused →) done.
   Clicking Play on idle starts a fresh loop; on paused resumes it.
3. Steps show: ○ pending | ⟳ running | ✓ ok | ✗ not_found/error.
4. "View in Reports" navigates to the reports tab via onViewReports callback.
5. Test tabs at the top are for quick visual scanning; clicking scrolls to
   that test section via a ref map.
================================================================================
*/

import React, { useRef } from 'react';
import type { IRStep } from '@plex/ir';
import { stepLabel } from '../ir/builder.js';
import type { RunConfig } from './RunConfigSheet.js';
import type { RelayStatus } from '../lib/runnerBridge.js';

export interface AiTriageEntry {
  status: 'loading' | 'done' | 'error';
  text: string;
}

export interface ExecutionQueueItem {
  testId: string;
  testName: string;
  tags?: string[];
  steps: IRStep[];
  status: 'pending' | 'running' | 'passed' | 'failed';
  stepStatuses: Record<string, 'ok' | 'not_found' | 'error'>;
  currentStepIdx: number | null;
  startedAt: number | null;
  finishedAt: number | null;
}

export type ExecutionRunStatus = 'idle' | 'running' | 'paused' | 'done';

type Props = {
  queue: ExecutionQueueItem[];
  runStatus: ExecutionRunStatus;
  passedTests: number;
  failedTests: number;
  runConfig?: RunConfig;
  /** Current relay connection status (for CLI-mode runs) */
  relayStatus?: RelayStatus;
  /** Label of the step currently executing (shown in mirror header via parent) */
  currentStepLabel?: string;
  /** Tier-2 Ollama: show "🤖 Analyze" on failed steps when enabled */
  tier2Enabled?: boolean;
  /** Tier-2: keyed by stepId — tracks triage state per step */
  aiTriageResults?: Map<string, AiTriageEntry>;
  /** Tier-2: called when user requests AI triage on a failed step */
  onRequestAiTriage?: (testId: string, step: IRStep) => void;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onReset: () => void;
  onViewReports: () => void;
  /** Connect to the plex run --studio-relay WS server */
  onConnectRelay: () => void;
  /** Disconnect from relay */
  onDisconnectRelay: () => void;
};

const TEST_STATUS_ICON: Record<string, string> = {
  pending: '○',
  running: '⟳',
  passed:  '✓',
  failed:  '✗',
};

const TEST_STATUS_COLOR: Record<string, string> = {
  pending: '#52525b',
  running: '#7c3aed',
  passed:  '#22c55e',
  failed:  '#ef4444',
};

const BROWSER_ICON: Record<string, string> = {
  chromium: '🔵',
  firefox:  '🦊',
  webkit:   '🧭',
};

export function ExecutionPanel({
  queue, runStatus, passedTests, failedTests, runConfig,
  relayStatus = 'disconnected', currentStepLabel,
  tier2Enabled = false, aiTriageResults, onRequestAiTriage,
  onPlay, onPause, onStop, onReset, onViewReports,
  onConnectRelay, onDisconnectRelay,
}: Props) {
  const testRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const totalSteps     = queue.reduce((n, q) => n + q.steps.length, 0);
  const completedSteps = queue.reduce((n, q) => n + Object.keys(q.stepStatuses).length, 0);
  const progressPct    = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  const totalTests     = queue.length;

  const scrollToTest = (testId: string) => {
    const el = testRefs.current.get(testId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isDone    = runStatus === 'done';
  const isRunning = runStatus === 'running';
  const isPaused  = runStatus === 'paused';
  const isIdle    = runStatus === 'idle';

  return (
    <div style={{
      flex: '0 0 50%',
      borderLeft: '1px solid #27272a',
      display: 'flex', flexDirection: 'column',
      background: '#0f0f11',
      overflow: 'hidden',
      minWidth: 340,
    }}>

      {/* ── Config badge + test tabs ── */}
      <div style={{
        borderBottom: '1px solid #27272a',
        overflowX: 'auto', overflowY: 'hidden',
        display: 'flex', alignItems: 'center',
        padding: '0 12px',
        flexShrink: 0, minHeight: 40,
        scrollbarWidth: 'none',
      }}>
        {/* Active run config badge */}
        {runConfig && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '3px 8px', marginRight: 8, flexShrink: 0,
            background: '#18181b', border: '1px solid #27272a',
            borderRadius: 20, fontSize: 10, color: '#71717a',
          }}>
            {runConfig.target === 'studio' ? (
              <span title="Studio: Chrome extension">🌐 Chrome</span>
            ) : (
              <>
                <span>{BROWSER_ICON[runConfig.browser] ?? '🔵'}</span>
                <span style={{ textTransform: 'capitalize' }}>{runConfig.browser}</span>
                {runConfig.headless && <span style={{ color: '#52525b' }}>· headless</span>}
              </>
            )}
            {runConfig.stepDelayMs > 0 && (
              <span style={{ color: '#52525b', marginLeft: 2 }}>· {runConfig.stepDelayMs}ms</span>
            )}
          </div>
        )}

        {queue.map((item) => {
          const color = TEST_STATUS_COLOR[item.status];
          return (
            <button
              key={item.testId}
              onClick={() => scrollToTest(item.testId)}
              style={{
                background: 'none', border: 'none',
                borderBottom: item.status === 'running' ? '2px solid #7c3aed' : '2px solid transparent',
                color, cursor: 'pointer', padding: '8px 10px',
                fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 5,
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 11 }}>{TEST_STATUS_ICON[item.status]}</span>
              {item.testName}
            </button>
          );
        })}
      </div>

      {/* ── CLI Relay banner (shown when CLI mode is selected) ── */}
      {runConfig?.target === 'cli' && (
        <RelayBanner
          relayStatus={relayStatus}
          browser={runConfig.browser}
          onConnect={onConnectRelay}
          onDisconnect={onDisconnectRelay}
        />
      )}

      {/* ── Progress bar ── */}
      <div style={{ height: 3, background: '#1c1c1f', flexShrink: 0 }}>
        <div style={{
          height: '100%',
          width: `${progressPct}%`,
          background: isDone && failedTests > 0 ? '#ef4444' : '#7c3aed',
          transition: 'width 0.3s ease',
        }} />
      </div>

      {/* ── Status line + controls ── */}
      <div style={{
        padding: '10px 14px',
        borderBottom: '1px solid #27272a',
        display: 'flex', alignItems: 'center', gap: 10,
        flexShrink: 0,
      }}>
        <span style={{ flex: 1, fontSize: 12, color: '#a1a1aa' }}>
          {isDone ? (
            <span>
              Run complete —&nbsp;
              <span style={{ color: '#22c55e', fontWeight: 600 }}>{passedTests} passed</span>
              ,&nbsp;
              <span style={{ color: failedTests > 0 ? '#ef4444' : '#71717a', fontWeight: 600 }}>{failedTests} failed</span>
            </span>
          ) : (
            `${completedSteps}/${totalSteps} steps · ${
              queue.filter(q => q.status === 'passed' || q.status === 'failed').length
            }/${totalTests} tests`
          )}
        </span>

        {isDone ? (
          <>
            <button onClick={onReset} style={ctrlBtn('#27272a', '#a1a1aa')}>↺ Reset</button>
            <button onClick={onViewReports} style={ctrlBtn('#7c3aed', '#fff')}>View in Reports →</button>
          </>
        ) : isIdle ? (
          <>
            <button onClick={onPlay} style={ctrlBtn('#7c3aed', '#fff')}>▶ Play</button>
          </>
        ) : isPaused ? (
          <>
            <button onClick={onPlay} style={ctrlBtn('#7c3aed', '#fff')}>▶ Resume</button>
            <button onClick={onStop} style={ctrlBtn('#7f1d1d', '#fca5a5')}>⏹ Stop</button>
          </>
        ) : (
          <>
            <button onClick={onPause} style={ctrlBtn('#27272a', '#fbbf24')}>⏸ Pause</button>
            <button onClick={onStop}  style={ctrlBtn('#7f1d1d', '#fca5a5')}>⏹ Stop</button>
          </>
        )}
      </div>

      {/* ── Scrollable test + step list ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {queue.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#3f3f46', fontSize: 13 }}>
            No tests queued. Select tests in Test Cases and click Run Selected.
          </div>
        ) : (
          queue.map((item) => (
            <TestCard
              key={item.testId}
              item={item}
              cardRef={(el) => {
                if (el) testRefs.current.set(item.testId, el);
                else testRefs.current.delete(item.testId);
              }}
              tier2Enabled={tier2Enabled}
              aiTriageResults={aiTriageResults}
              onRequestAiTriage={onRequestAiTriage}
            />
          ))
        )}
      </div>

    </div>
  );
}

// ── RelayBanner ───────────────────────────────────────────────────────────────

function RelayBanner({ relayStatus, browser, onConnect, onDisconnect }: {
  relayStatus: RelayStatus;
  browser: string;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const STATUS_COLOR: Record<RelayStatus, string> = {
    disconnected: '#52525b',
    connecting:   '#eab308',
    connected:    '#22c55e',
    error:        '#ef4444',
  };

  const STATUS_LABEL: Record<RelayStatus, string> = {
    disconnected: 'Not connected to plex run',
    connecting:   'Connecting…',
    connected:    'Connected — receiving screenshots',
    error:        'Connection error — retry?',
  };

  return (
    <div style={{
      padding: '8px 14px',
      borderBottom: '1px solid #27272a',
      display: 'flex', alignItems: 'center', gap: 10,
      background: relayStatus === 'connected' ? 'rgba(34,197,94,0.05)' : '#0d0d10',
      flexShrink: 0,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_COLOR[relayStatus], flexShrink: 0 }} />
      <span style={{ fontSize: 11, color: '#71717a', flex: 1 }}>
        {STATUS_LABEL[relayStatus]}
      </span>

      {relayStatus === 'connected' ? (
        <button onClick={onDisconnect} style={relayBtn('#27272a', '#71717a')}>Disconnect</button>
      ) : (
        <>
          <span style={{ fontSize: 10, color: '#3f3f46' }}>
            Run: <code style={{ color: '#a78bfa' }}>plex run ... --studio-relay</code>
          </span>
          <button
            onClick={onConnect}
            disabled={relayStatus === 'connecting'}
            style={relayBtn('#7c3aed', '#fff')}
          >
            {relayStatus === 'connecting' ? 'Connecting…' : relayStatus === 'error' ? 'Retry' : 'Connect to CLI Run'}
          </button>
        </>
      )}
    </div>
  );
}

function relayBtn(bg: string, color: string): React.CSSProperties {
  return { background: bg, color, border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0 };
}

// ── AI Triage Panel ───────────────────────────────────────────────────────────

function AiTriagePanel({ entry, onDismiss }: { entry: AiTriageEntry; onDismiss: () => void }) {
  return (
    <div style={{
      margin: '0 14px 8px 28px',
      background: 'rgba(124,58,237,0.08)',
      border: '1px solid rgba(124,58,237,0.25)',
      borderRadius: 6, padding: '10px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>🤖 AI Analysis</span>
        {entry.status === 'loading' && (
          <span style={{ fontSize: 10, color: '#7c3aed', animation: 'pulse 1s ease-in-out infinite' }}>generating…</span>
        )}
        {entry.status === 'error' && (
          <span style={{ fontSize: 10, color: '#f87171' }}>error</span>
        )}
        <button onClick={onDismiss} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: 12, padding: '0 2px' }}>✕</button>
      </div>
      <div style={{
        fontSize: 12, color: '#c4b5fd', lineHeight: 1.6, whiteSpace: 'pre-wrap',
        maxHeight: 200, overflowY: 'auto',
        fontFamily: entry.status === 'loading' ? 'inherit' : 'inherit',
      }}>
        {entry.text || (entry.status === 'loading' ? '▊' : entry.status === 'error' ? 'Failed to get AI analysis. Check Ollama is running and the model is available.' : '')}
        {entry.status === 'loading' && entry.text && <span style={{ animation: 'pulse 1s ease-in-out infinite' }}>▊</span>}
      </div>
    </div>
  );
}

// ── TestCard ──────────────────────────────────────────────────────────────────

function TestCard({ item, cardRef, tier2Enabled, aiTriageResults, onRequestAiTriage }: {
  item: ExecutionQueueItem;
  cardRef: (el: HTMLDivElement | null) => void;
  tier2Enabled?: boolean;
  aiTriageResults?: Map<string, AiTriageEntry>;
  onRequestAiTriage?: (testId: string, step: IRStep) => void;
}) {
  const color = TEST_STATUS_COLOR[item.status];
  const icon  = TEST_STATUS_ICON[item.status];
  const stepsDone = Object.keys(item.stepStatuses).length;
  const durationMs = item.startedAt && item.finishedAt ? item.finishedAt - item.startedAt : null;

  // Local dismissed set — user can close the AI panel per step
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set());

  return (
    <div
      ref={cardRef}
      style={{
        borderBottom: '1px solid #1c1c1f',
        borderLeft: `3px solid ${item.status === 'running' ? '#7c3aed' : item.status === 'passed' ? '#22c55e' : item.status === 'failed' ? '#ef4444' : 'transparent'}`,
      }}
    >
      {/* Test header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px',
        background: item.status === 'passed' ? 'rgba(34,197,94,0.05)' : item.status === 'failed' ? 'rgba(239,68,68,0.05)' : 'transparent',
      }}>
        <span style={{ color, fontSize: 14, flexShrink: 0, animation: item.status === 'running' ? 'spin 1s linear infinite' : undefined }}>{icon}</span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#e4e4e7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.testName}
        </span>
        <span style={{ fontSize: 11, color: '#52525b', flexShrink: 0 }}>
          {stepsDone}/{item.steps.length} steps
          {durationMs !== null && ` · ${(durationMs / 1000).toFixed(1)}s`}
        </span>
      </div>

      {/* Step rows */}
      {item.steps.map((step, si) => {
        const stepStatus = item.stepStatuses[step.id];
        const isCurrent  = item.currentStepIdx === si && item.status === 'running';
        const isFailed   = stepStatus === 'not_found' || stepStatus === 'error';

        let statusIcon  = '○';
        let statusColor = '#3f3f46';
        if (isCurrent)              { statusIcon = '⟳'; statusColor = '#7c3aed'; }
        else if (stepStatus === 'ok')        { statusIcon = '✓'; statusColor = '#22c55e'; }
        else if (stepStatus === 'not_found') { statusIcon = '✗'; statusColor = '#ef4444'; }
        else if (stepStatus === 'error')     { statusIcon = '⚠'; statusColor = '#f59e0b'; }

        const label = stepLabel(step);

        // Get primary strategy badge
        let strategy: string | null = null;
        if ('selector' in step && step.selector) {
          const sel = step.selector as { candidates: Array<{ strategy: string }> };
          strategy = sel.candidates[0]?.strategy ?? null;
        }

        const triageEntry = aiTriageResults?.get(step.id);
        const showTriage  = triageEntry && !dismissed.has(step.id);

        return (
          <React.Fragment key={step.id}>
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 14px 5px 28px',
                background: isCurrent ? 'rgba(124,58,237,0.07)' : 'transparent',
                borderTop: '1px solid #111113',
              }}
            >
              <span style={{ fontSize: 11, color: '#52525b', minWidth: 18, textAlign: 'right', flexShrink: 0 }}>{si + 1}</span>
              <span style={{ fontSize: 11, color: '#71717a', flexShrink: 0, minWidth: 70 }}>{step.type}</span>
              <span style={{ flex: 1, fontSize: 11, color: '#a1a1aa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label}
              </span>
              {strategy && (
                <span style={{
                  fontSize: 9, color: statusColor === '#22c55e' ? '#22c55e' : '#71717a',
                  border: `1px solid ${statusColor === '#22c55e' ? '#22c55e' : '#3f3f46'}`,
                  borderRadius: 3, padding: '1px 4px', flexShrink: 0,
                }}>
                  {strategy}
                </span>
              )}
              {/* AI Analyze button — shown on failed steps when Tier-2 is enabled */}
              {tier2Enabled && isFailed && item.status !== 'running' && !triageEntry && (
                <button
                  onClick={() => onRequestAiTriage?.(item.testId, step)}
                  title="Ask local AI why this step failed"
                  style={{
                    background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.4)',
                    borderRadius: 4, color: '#a78bfa', cursor: 'pointer',
                    fontSize: 10, fontWeight: 600, padding: '1px 6px', flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  🤖 Analyze
                </button>
              )}
              {tier2Enabled && triageEntry?.status === 'loading' && (
                <span style={{ fontSize: 10, color: '#7c3aed', flexShrink: 0 }}>🤖 ⟳</span>
              )}
              <span style={{ fontSize: 12, color: statusColor, flexShrink: 0, minWidth: 14, textAlign: 'center' }}>{statusIcon}</span>
            </div>

            {/* AI triage result panel */}
            {showTriage && (
              <AiTriagePanel
                entry={triageEntry}
                onDismiss={() => setDismissed(prev => new Set([...prev, step.id]))}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ctrlBtn(bg: string, color: string): React.CSSProperties {
  return {
    background: bg, color, border: 'none',
    borderRadius: 6, padding: '5px 12px',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    flexShrink: 0,
  };
}
